import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Sparkles, ArrowRight, School, ArrowLeft, UserCircle } from 'lucide-react';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DEFAULT_IMAGE = 'https://images.unsplash.com/photo-1503676260728-1c00da094a0b?auto=format&fit=crop&w=1260&q=80';

export default function ParentLogin() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [step, setStep] = useState(1);
  const [schoolCode, setSchoolCode] = useState(searchParams.get('code') || '');
  const [schoolData, setSchoolData] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [codeError, setCodeError] = useState('');

  const [mode, setMode] = useState('login'); // login or register
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [relation, setRelation] = useState('father');
  const [admissionNumber, setAdmissionNumber] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (searchParams.get('code')) handleVerifyCode(searchParams.get('code'));
  }, []);

  const handleVerifyCode = async (code) => {
    const cleaned = (code || schoolCode).trim().toUpperCase();
    if (!cleaned || cleaned.length < 3) { setCodeError('Please enter a valid school code'); return; }
    setVerifying(true);
    setCodeError('');
    try {
      const res = await axios.get(`${API_URL}/auth/verify-school-code/${cleaned}`);
      setSchoolData(res.data.data);
      setSchoolCode(cleaned);
      setStep(2);
    } catch {
      setCodeError('School code not found. Check with your school.');
    } finally {
      setVerifying(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/parent/login`, { email, password, school_code: schoolCode });
      const { access_token, refresh_token, user } = res.data.data;
      localStorage.setItem('access_token', access_token);
      localStorage.setItem('refresh_token', refresh_token);
      localStorage.setItem('parent_user', JSON.stringify(user));
      toast.success('Welcome back!');
      navigate('/parent/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/parent/register`, {
        email, password, full_name: fullName, phone, relation,
        student_admission_number: admissionNumber, school_code: schoolCode,
      });
      const { access_token, refresh_token, student } = res.data.data;
      localStorage.setItem('access_token', access_token);
      localStorage.setItem('refresh_token', refresh_token);
      toast.success(`Registered! Linked to ${student.full_name} (${student.class_name} ${student.section})`);
      navigate('/parent/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    setStep(1);
    setSchoolData(null);
    setCodeError('');
    setEmail('');
    setPassword('');
  };

  const primaryColor = schoolData?.branding?.primary_color || '#7c3aed';
  const bgColor = schoolData?.branding?.secondary_color || '#0f0a1e';
  const brandName = schoolData?.branding?.school_name || schoolData?.school_name || '';
  const tagline = schoolData?.branding?.tagline || '';

  return (
    <div className="min-h-screen flex" data-testid="parent-login-page">
      {/* Left Image */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <img src={DEFAULT_IMAGE} alt="Students" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-black/10" />
        <div className="relative z-10 flex flex-col justify-end p-12 pb-16 w-full">
          <div className="flex items-center gap-2.5 mb-5">
            <div className="h-10 w-10 rounded-xl bg-violet-500 flex items-center justify-center shadow-lg">
              <UserCircle className="h-5 w-5 text-white" />
            </div>
            <span className="text-2xl font-bold text-white tracking-tight">Ariskool Parent</span>
          </div>
          <h2 className="text-3xl font-bold text-white leading-snug mb-3">Stay Connected<br />With Your Child's School</h2>
          <p className="text-white/60 text-sm max-w-md leading-relaxed">Track attendance, view grades, pay fees, and stay updated with announcements — all from one portal.</p>
        </div>
      </div>

      {/* Right Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 sm:p-10 relative overflow-hidden transition-colors duration-500" style={{ background: bgColor }} data-testid="parent-form-panel">
        <div className="absolute top-[-200px] right-[-100px] w-[400px] h-[400px] rounded-full blur-[120px] pointer-events-none" style={{ background: `${primaryColor}12` }} />

        <div className="w-full max-w-md relative z-10">

          {/* Step 1: School Code */}
          {step === 1 && (
            <div data-testid="parent-login-step-1">
              <div className="text-center mb-8 lg:hidden">
                <div className="inline-flex items-center gap-2.5 mb-4">
                  <div className="h-10 w-10 rounded-xl bg-violet-500 flex items-center justify-center"><UserCircle className="h-5 w-5 text-white" /></div>
                  <span className="text-2xl font-bold text-white">Ariskool Parent</span>
                </div>
              </div>
              <div className="mb-8">
                <h1 className="text-2xl font-bold text-white mb-2" data-testid="parent-login-title">Parent Portal</h1>
                <p className="text-sm text-white/50">Enter your child's school code to continue</p>
              </div>
              <div className="bg-white/[0.04] border border-white/[0.08] rounded-2xl p-6 backdrop-blur-xl">
                <form onSubmit={(e) => { e.preventDefault(); handleVerifyCode(); }} className="space-y-5">
                  <div className="space-y-2">
                    <Label className="text-white/60 text-xs font-medium">School Code</Label>
                    <div className="relative">
                      <School className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/30" />
                      <Input type="text" placeholder="e.g. DPS-2847" value={schoolCode} onChange={(e) => { setSchoolCode(e.target.value.toUpperCase()); setCodeError(''); }} autoFocus className="h-12 pl-10 bg-white/[0.06] border-white/[0.1] text-white rounded-xl placeholder:text-white/25 focus:border-violet-500/50 uppercase tracking-[0.15em] font-mono text-center text-lg" data-testid="parent-school-code-input" />
                    </div>
                    {codeError && <p className="text-xs text-red-400/80 mt-1" data-testid="parent-code-error">{codeError}</p>}
                  </div>
                  <Button type="submit" className="w-full h-11 font-semibold rounded-xl shadow-lg text-white bg-violet-500 hover:bg-violet-400" disabled={verifying || !schoolCode.trim()} data-testid="parent-verify-code-btn">
                    {verifying ? 'Verifying...' : <><span>Continue</span><ArrowRight className="ml-2 h-4 w-4" /></>}
                  </Button>
                </form>
                <p className="text-xs text-white/20 text-center mt-4">Ask your school for the code</p>
              </div>
              <p className="mt-6 text-center text-sm text-white/40">
                Staff login?{' '}<Link to="/login" className="font-medium text-violet-400 hover:text-violet-300" data-testid="staff-login-link">Sign in here</Link>
              </p>
            </div>
          )}

          {/* Step 2: Credentials */}
          {step === 2 && schoolData && (
            <div data-testid="parent-login-step-2">
              <div className="text-center mb-6" data-testid="parent-school-branding">
                <div className="h-14 w-14 rounded-xl mx-auto mb-3 flex items-center justify-center shadow-lg" style={{ background: primaryColor }}>
                  <span className="text-white text-xl font-bold">{brandName.charAt(0)}</span>
                </div>
                <h2 className="text-lg font-bold text-white">{brandName}</h2>
                {tagline && <p className="text-xs text-white/40 mt-1">{tagline}</p>}
                <p className="text-xs text-white/25 font-mono mt-1.5 tracking-wider">{schoolCode}</p>
              </div>

              {/* Toggle Login / Register */}
              <div className="flex bg-white/[0.06] rounded-xl p-1 mb-5">
                <button onClick={() => setMode('login')} className={`flex-1 py-2 text-sm font-medium rounded-lg transition-colors ${mode === 'login' ? 'bg-violet-500 text-white shadow' : 'text-white/40 hover:text-white/60'}`} data-testid="parent-mode-login">Sign In</button>
                <button onClick={() => setMode('register')} className={`flex-1 py-2 text-sm font-medium rounded-lg transition-colors ${mode === 'register' ? 'bg-violet-500 text-white shadow' : 'text-white/40 hover:text-white/60'}`} data-testid="parent-mode-register">Register</button>
              </div>

              <div className="bg-white/[0.04] border border-white/[0.08] rounded-2xl p-6 backdrop-blur-xl">
                {mode === 'login' ? (
                  <form onSubmit={handleLogin} className="space-y-4" data-testid="parent-login-form">
                    <div className="space-y-2">
                      <Label className="text-white/60 text-xs">Email</Label>
                      <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="parent@email.com" className="h-11 bg-white/[0.06] border-white/[0.1] text-white rounded-xl placeholder:text-white/25" data-testid="parent-email-input" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-white/60 text-xs">Password</Label>
                      <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required placeholder="Enter password" className="h-11 bg-white/[0.06] border-white/[0.1] text-white rounded-xl placeholder:text-white/25" data-testid="parent-password-input" />
                    </div>
                    <Button type="submit" className="w-full h-11 font-semibold rounded-xl text-white" style={{ background: primaryColor }} disabled={loading} data-testid="parent-login-btn">
                      {loading ? 'Signing in...' : 'Sign In'}
                    </Button>
                  </form>
                ) : (
                  <form onSubmit={handleRegister} className="space-y-3" data-testid="parent-register-form">
                    <div className="space-y-2">
                      <Label className="text-white/60 text-xs">Student Admission Number *</Label>
                      <Input value={admissionNumber} onChange={(e) => setAdmissionNumber(e.target.value)} required placeholder="e.g. ADM-D4F2F8" className="h-10 bg-white/[0.06] border-white/[0.1] text-white rounded-xl placeholder:text-white/25 font-mono uppercase" data-testid="parent-admission-input" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-2">
                        <Label className="text-white/60 text-xs">Full Name *</Label>
                        <Input value={fullName} onChange={(e) => setFullName(e.target.value)} required placeholder="Your name" className="h-10 bg-white/[0.06] border-white/[0.1] text-white rounded-xl placeholder:text-white/25" data-testid="parent-fullname-input" />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-white/60 text-xs">Relation</Label>
                        <select value={relation} onChange={(e) => setRelation(e.target.value)} className="h-10 w-full rounded-xl bg-white/[0.06] border border-white/[0.1] text-white text-sm px-3" data-testid="parent-relation-select">
                          <option value="father" className="text-black">Father</option>
                          <option value="mother" className="text-black">Mother</option>
                          <option value="guardian" className="text-black">Guardian</option>
                        </select>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-white/60 text-xs">Email *</Label>
                      <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="parent@email.com" className="h-10 bg-white/[0.06] border-white/[0.1] text-white rounded-xl placeholder:text-white/25" data-testid="parent-reg-email" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-2">
                        <Label className="text-white/60 text-xs">Phone</Label>
                        <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+91 98765 43210" className="h-10 bg-white/[0.06] border-white/[0.1] text-white rounded-xl placeholder:text-white/25" data-testid="parent-phone-input" />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-white/60 text-xs">Password *</Label>
                        <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={8} placeholder="Min 8 chars" className="h-10 bg-white/[0.06] border-white/[0.1] text-white rounded-xl placeholder:text-white/25" data-testid="parent-reg-password" />
                      </div>
                    </div>
                    <Button type="submit" className="w-full h-11 font-semibold rounded-xl text-white mt-2" style={{ background: primaryColor }} disabled={loading} data-testid="parent-register-btn">
                      {loading ? 'Registering...' : 'Register & Link Child'}
                    </Button>
                  </form>
                )}
              </div>

              <button onClick={handleBack} className="mt-5 flex items-center justify-center gap-2 w-full text-sm text-white/30 hover:text-white/50 transition-colors" data-testid="parent-back-btn">
                <ArrowLeft className="h-3.5 w-3.5" /> Use a different school code
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
